import { Button, FormLabel, OptionType, SearchableSelect, TagSelect } from "shared";
import { ILegalBankDetail } from "entities/template";
import type { ILegalOrg, ILegalOrgBankDetail } from "entities/legal_orgs";
import React, { useEffect, useMemo, useState } from "react";
import { useComponentsTranslations } from "../../../../../lib/i18n";
import { joinBankOptionLabelParts } from "../../../../../lib/utils/bank_detail_option_label";
import styles from "./legal_bank_details_modal.module.scss";

const bankIdStr = (id: string | number) => String(id);

const PRIORITY_CURRENCIES = ["RUB", "USD", "EUR", "CNY"] as const;

function typeContainsPrimaryAccount(type: string | undefined): boolean {
    return (type ?? "").toLowerCase().includes("основной");
}

/** Внутри одной валюты: раньше точное «Основной», затем «Основной (…)» и т.п. */
function accountTypePrimaryLabelRank(type: string | undefined): number {
    const t = (type ?? "").trim().toLowerCase();
    if (t === "основной") {
        return 0;
    }
    if (t.includes("основной")) {
        return 1;
    }
    return 2;
}

function isRubCurrency(currency: string | undefined): boolean {
    return String(currency ?? "").toUpperCase() === "RUB";
}

/** 0 — RUB + «основной»; 1 — остальные «основной»; 2 — прочие. */
function bankDetailModalTier(d: ILegalOrgBankDetail): number {
    const primary = typeContainsPrimaryAccount(d.ya_account_type);
    if (isRubCurrency(d.ya_account_currency) && primary) return 0;
    if (primary) return 1;
    return 2;
}

function currencyPriorityRank(currency: string | undefined): number {
    const u = String(currency ?? "").toUpperCase();
    const i = PRIORITY_CURRENCIES.indexOf(u as (typeof PRIORITY_CURRENCIES)[number]);
    return i >= 0 ? i : PRIORITY_CURRENCIES.length;
}

function compareLegalBankDetailsForModal(a: ILegalOrgBankDetail, b: ILegalOrgBankDetail): number {
    const ta = bankDetailModalTier(a);
    const tb = bankDetailModalTier(b);
    if (ta !== tb) return ta - tb;

    const ra = currencyPriorityRank(a.ya_account_currency);
    const rb = currencyPriorityRank(b.ya_account_currency);
    if (ra !== rb) return ra - rb;

    const ca = String(a.ya_account_currency ?? "").toUpperCase();
    const cb = String(b.ya_account_currency ?? "").toUpperCase();
    if (ca !== cb) return ca.localeCompare(cb);

    const pa = accountTypePrimaryLabelRank(a.ya_account_type);
    const pb = accountTypePrimaryLabelRank(b.ya_account_type);
    if (pa !== pb) return pa - pb;

    return bankIdStr(a.id).localeCompare(bankIdStr(b.id));
}

interface ILegalBankDetailsModalProps {
    close: () => void;
    organization: ILegalOrg;
    /** ya_id компании — ключ в listLegalBankDetails */
    companyYaId: string;
    bankBlackListArray: string[];
    bankDefaultValue?: string;
    onSave: (value: ILegalBankDetail) => void;
}

export const LegalBankDetailsModal: React.FC<ILegalBankDetailsModalProps> = React.memo(
    ({ close, organization, companyYaId, bankBlackListArray, bankDefaultValue, onSave }) => {
        const t = useComponentsTranslations();

        const allDetails = useMemo(() => {
            const raw = organization.ya_bank_details ?? [];
            return [...raw].sort(compareLegalBankDetailsForModal);
        }, [organization.ya_bank_details]);

        const allIds = useMemo(() => allDetails.map((d) => bankIdStr(d.id)), [allDetails]);

        const [includedIds, setIncludedIds] = useState<string[]>([]);
        const [defaultBankId, setDefaultBankId] = useState<string | undefined>(bankDefaultValue);

        useEffect(() => {
            const bl = new Set((bankBlackListArray ?? []).map(bankIdStr));
            const nextIncluded = allIds.filter((id) => !bl.has(id));
            setIncludedIds(nextIncluded);
            const def = bankDefaultValue && nextIncluded.includes(bankDefaultValue) ? bankDefaultValue : undefined;
            setDefaultBankId(def);
        }, [organization, companyYaId, bankBlackListArray, bankDefaultValue, allIds.join("|")]);

        const constructOptions = useMemo(
            () =>
                allDetails.map((d) => ({
                    value: bankIdStr(d.id),
                    label: joinBankOptionLabelParts(
                        d.ya_account_currency,
                        d.ya_account_type,
                        d.ya_bank_name,
                        d.ya_payment_account,
                        d.ya_corr_account
                    ),
                })),
            [allDetails]
        );

        const onChangeIncluded = (selected: string[]) => {
            setIncludedIds(selected);
            if (defaultBankId && !selected.includes(defaultBankId)) {
                setDefaultBankId(undefined);
            }
        };

        const onSaveBtnClick = () => {
            const removed = allIds.filter((id) => !includedIds.includes(id));
            onSave({
                id: companyYaId,
                yaName: organization.ya_name,
                bankBlackListArray: removed,
                bankDefaultValue: defaultBankId,
            });
            close();
        };

        return (
            <div className={styles.container}>
                <TagSelect
                    value={includedIds}
                    options={constructOptions}
                    placeholder={t("bank")}
                    onChange={onChangeIncluded}
                />
                <FormLabel title={t("defaultValue")}>
                    <SearchableSelect
                        options={constructOptions.filter((o) => includedIds.includes(o.value))}
                        placeholder={t("no")}
                        optionValue={
                            defaultBankId
                                ? (constructOptions.find((o) => o.value === defaultBankId) as OptionType)
                                : undefined
                        }
                        onChange={(val) => setDefaultBankId(val ? String(val) : undefined)}
                        isMulti={false}
                        hideClearIcon={false}
                        isSearchable={true}
                    />
                </FormLabel>
                <div className={styles.buttonGroup}>
                    <Button className={styles.button} view="legal" onClick={onSaveBtnClick}>
                        {t("save")}
                    </Button>
                </div>
            </div>
        );
    }
);
